import { useContext, useEffect, useId, useState } from 'react'
import { Check, Copy, Workflow } from 'lucide-react'
import type { Mermaid } from 'mermaid'
import { useCopy } from '@/hooks/useCopy'
import { DiagramStreamingContext } from './diagramContext'

// Mermaid is large, so it is fetched the first time an answer contains a diagram.
let loader: Promise<Mermaid> | null = null
const loadMermaid = () => (loader ??= import('mermaid').then((m) => m.default))

/** Design tokens are stored as "r g b"; Mermaid derives its shades from hex colours. */
function token(name: string): string {
  const rgb = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim().split(/\s+/)
  return `#${rgb.map((c) => Number(c).toString(16).padStart(2, '0')).join('')}`
}

function themeVariables(dark: boolean) {
  const ink = token('ink')
  const muted = token('ink-muted')
  const surface = token('surface')
  const accent = token('accent')
  const accentSoft = token('accent-soft')
  const panel = token('panel')
  const line = token('line-strong')
  return {
    darkMode: dark,
    fontFamily: 'Inter, InterVariable, ui-sans-serif, system-ui, sans-serif',
    fontSize: '14px',
    background: surface,
    textColor: ink,
    lineColor: muted,
    // flowchart
    primaryColor: accentSoft,
    primaryBorderColor: accent,
    primaryTextColor: ink,
    secondaryColor: panel,
    secondaryBorderColor: line,
    secondaryTextColor: ink,
    tertiaryColor: panel,
    tertiaryBorderColor: line,
    tertiaryTextColor: ink,
    clusterBkg: panel,
    clusterBorder: line,
    edgeLabelBackground: surface,
    // sequence diagrams
    actorBkg: accentSoft,
    actorBorder: accent,
    actorTextColor: ink,
    actorLineColor: line,
    signalColor: muted,
    signalTextColor: ink,
    labelBoxBkgColor: panel,
    labelBoxBorderColor: line,
    labelTextColor: ink,
    loopTextColor: ink,
    noteBkgColor: token('warning-soft'),
    noteBorderColor: line,
    noteTextColor: ink,
    activationBkgColor: panel,
    activationBorderColor: line,
  }
}

/** Follows the `dark` class the theme toggle sets on <html>, so diagrams redraw in the new palette. */
function useDarkClass(): boolean {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))
  useEffect(() => {
    const root = document.documentElement
    const observer = new MutationObserver(() => setDark(root.classList.contains('dark')))
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return dark
}

type State = { status: 'drawing' } | { status: 'ready'; svg: string } | { status: 'failed' }

/** A ```mermaid block from an answer, drawn as a diagram (falls back to its text if it can't be drawn). */
export function MermaidDiagram({ source }: { source: string }) {
  const streaming = useContext(DiagramStreamingContext)
  const dark = useDarkClass()
  const id = `diagram-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const { copied, copy } = useCopy()
  const [state, setState] = useState<State>({ status: 'drawing' })

  useEffect(() => {
    if (streaming) return
    let cancelled = false
    setState({ status: 'drawing' })
    loadMermaid()
      .then(async (mermaid) => {
        mermaid.initialize({
          startOnLoad: false,
          // Labels are escaped and links/click handlers disabled: answer text is never trusted as markup.
          securityLevel: 'strict',
          suppressErrorRendering: true,
          theme: 'base',
          themeVariables: themeVariables(dark),
          flowchart: { curve: 'basis', padding: 12 },
        })
        const { svg } = await mermaid.render(id, source.trim())
        if (!cancelled) setState({ status: 'ready', svg })
      })
      .catch(() => !cancelled && setState({ status: 'failed' }))
    return () => {
      cancelled = true
    }
  }, [source, dark, streaming, id])

  const drawing = streaming || state.status === 'drawing'

  return (
    <figure className="my-5 overflow-hidden rounded-2xl border border-line bg-surface" aria-busy={drawing}>
      <div className="flex h-9 items-center justify-between border-b border-line pl-3 pr-1.5 text-xs text-ink-faint">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Workflow className="size-3.5" aria-hidden />
          Diagram
        </span>
        <button
          type="button"
          onClick={() => void copy(source)}
          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 font-medium text-ink-muted transition-colors hover:bg-subtle hover:text-ink"
          aria-label={copied ? 'Copied diagram source' : 'Copy diagram source'}
        >
          {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {drawing ? (
        <div className="flex min-h-40 items-center justify-center p-4 text-sm text-ink-faint">
          <span className="motion-safe:animate-pulse">Drawing diagram…</span>
        </div>
      ) : state.status === 'ready' ? (
        <div
          role="img"
          aria-label="Diagram"
          className="flex justify-center overflow-x-auto p-4 [&_svg]:h-auto [&_svg]:max-w-full"
          // Rendered by Mermaid with securityLevel "strict" (its output is sanitized with DOMPurify).
          dangerouslySetInnerHTML={{ __html: state.svg }}
        />
      ) : (
        <div className="p-3.5">
          <p className="mb-2 text-xs text-ink-faint">This diagram couldn’t be drawn, so here is its outline.</p>
          <pre className="overflow-x-auto font-mono text-[13px] leading-6 text-ink">{source}</pre>
        </div>
      )}
    </figure>
  )
}
